import React, { useState } from 'react';
import { graphql } from 'relay-runtime';
import '../../../stylesheets/components/globals.module.scss';
import '../../../stylesheets/components/theme.module.scss';
import '../../../stylesheets/components/buttonGlobals.module.scss';
import '../../../stylesheets/components/dropdownMenus.module.scss';
import '../../../stylesheets/components/dialog.module.scss';
import Text, { TextFieldGlimmer } from '../tenant_portal/fields/Text';
import PortalButton from '../components/PortalButton';
import {
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay';
import { ChangePasswordLoginMutation$data } from './__generated__/ChangePasswordLoginMutation.graphql';
import { ErrorType } from '../components/Types';
import PageHeader, {
  PageHeaderGlimmer,
} from '../tenant_portal/shared/PageHeader';
import SubmitSpinner from '../components/SubmitSpinner';
import { FIELD_MODES, updateDeep } from '../components/fields/FieldTools';
import { FieldsDeepUpdate } from '../typescript/types/FieldComponentProps';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import type {
  ChangePasswordQuery as ChangePasswordQueryType,
  ChangePasswordQuery$data as ChangePasswordQueryResponse,
} from './__generated__/ChangePasswordQuery.graphql';
import SuccessPage from '../tenant_portal/shared/SuccessPage';
import { PortalButtonGlimmer } from '../components/PortalGlimmerElements';
import { filterErrorsByPath, formatErrorMessages } from '../components/Tools';
import loginStyles from '../../../stylesheets/components/login.module.scss';
import SomethingWentWrongAlert from './SomethingWentWrongAlert';

export const ChangePasswordQuery = graphql`
  query ChangePasswordQuery($token: String!) {
    login(passwordResetToken: $token) {
      id
    }
  }
`;

const LoginChangePasswordMutation = graphql`
  mutation ChangePasswordLoginMutation($input: LoginChangePasswordInput!) {
    loginChangePassword(input: $input) {
      errors {
        path
        messages
      }
    }
  }
`;

const ChangePassword = () => {
  const { resetPasswordToken } = useParams();
  const initialData = {
    password: '',
    passwordConfirmation: '',
  };
  const [resetPasswordFields, setResetPasswordFields] = useState(initialData);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ErrorType[]>([]);

  const baseErrors = filterErrorsByPath(errors);
  const formattedBaseErrorMessages = formatErrorMessages(baseErrors, '').trim();
  const somethingWentWrongAlert = (
    <SomethingWentWrongAlert
      body={formattedBaseErrorMessages}
      resetKey={errors}
    />
  );

  const loaderQueryReference = useLoaderData() as PreloadedQuery<
    ChangePasswordQueryType,
    ChangePasswordQueryResponse
  >;

  const [queryReference] = useQueryLoader<ChangePasswordQueryType>(
    ChangePasswordQuery,
    loaderQueryReference
  );

  const loginActivationData = usePreloadedQuery<ChangePasswordQueryType>(
    ChangePasswordQuery,
    queryReference as PreloadedQuery<
      ChangePasswordQueryType,
      ChangePasswordQueryResponse
    >
  );

  const [commitMutation, isMutationInFlight] = useMutation(
    LoginChangePasswordMutation
  );

  const login = loginActivationData.login;
  if (!login) {
    return <PageHeader title="Invalid or expired activation token" />;
  }

  const handleResetPasswordFieldChange: FieldsDeepUpdate = (
    fieldName,
    value
  ) => {
    setResetPasswordFields((prevFields) => {
      return updateDeep(prevFields, fieldName, value);
    });
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleSubmit = () => {
    const blankErrors: ErrorType[] = [];
    Object.entries(resetPasswordFields).forEach(([key, value]) => {
      if (value === '') {
        blankErrors.push({
          path: key,
          messages: ["can't be blank"],
        });
      }
    });

    if (blankErrors.length > 0) {
      setErrors(blankErrors);
      return;
    }

    commitMutation({
      variables: {
        input: {
          login: {
            password: resetPasswordFields.password,
            passwordConfirmation: resetPasswordFields.passwordConfirmation,
            resetPasswordToken: resetPasswordToken,
          },
        },
      },
      onCompleted: (response) => {
        const responseData = response as ChangePasswordLoginMutation$data;
        console.log('completed! response: ', responseData);
        if (!responseData || !responseData.loginChangePassword) {
          setErrors([
            {
              path: 'base',
              messages: [
                'There was an error. Please contact Property Matrix support for assistance.',
              ],
            },
          ]);
          return;
        }
        if (
          responseData.loginChangePassword.errors &&
          responseData.loginChangePassword.errors.length > 0
        ) {
          setErrors(responseData.loginChangePassword.errors as ErrorType[]);
          return;
        }
        setIsPasswordReset(true);
      },
      onError: (error) => {
        setErrors([
          {
            path: 'base',
            messages: [
              'There was an error. Please contact Property Matrix support for assistance.',
            ],
          },
        ]);
        console.error(error);
      },
    });
  };

  if (isMutationInFlight)
    return <SubmitSpinner spinnerText={'Resetting password...'} />;

  if (isPasswordReset) {
    return (
      <SuccessPage
        successText={'Password reset'}
        successSubText={'Your password has been reset. Please log back in.'}
        variant="login-success"
      >
        <PortalButton
          onClick={() => navigate('../email')}
          extraClasses={[loginStyles.loginPrimaryButton]}
        >
          Back to Login
        </PortalButton>
      </SuccessPage>
    );
  }
  return (
    <div className={loginStyles.loginRouteContent}>
      {somethingWentWrongAlert}
      <PageHeader title="Reset password" includeBottomMargin={false} />
      <form className={loginStyles.loginForm} onSubmit={handleFormSubmit}>
        <Text
          field={{
            label: 'Password',
            name: 'password',
            type: 'password',
            value: resetPasswordFields.password,
            autocomplete: 'password',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleResetPasswordFieldChange}
          errors={filterErrorsByPath(errors, ['password'])}
        />
        <Text
          field={{
            label: 'Confirm Password',
            name: 'passwordConfirmation',
            type: 'password',
            value: resetPasswordFields.passwordConfirmation,
            autocomplete: 'password',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleResetPasswordFieldChange}
          errors={filterErrorsByPath(errors, ['passwordConfirmation'])}
        />
        <button type="submit" hidden /> {/* Allows submit on enter */}
      </form>
      <PortalButton
        onClick={handleSubmit}
        extraClasses={[loginStyles.loginPrimaryButton]}
      >
        Reset password
      </PortalButton>
    </div>
  );
};

export const ChangePasswordGlimmer = () => {
  return (
    <>
      <PageHeaderGlimmer />
      <TextFieldGlimmer />
      <TextFieldGlimmer />
      <PortalButtonGlimmer />
    </>
  );
};

export default ChangePassword;
